import '@babel/polyfill';
import 'url-polyfill';
import 'helpers/attachTruncateByChars';
import 'helpers/inboxFilterStorage';

// Suppress warnings
const originalConsoleError = console.error;
const originalConsoleWarn = console.warn;

console.error = (...args) => {
  const message = args.join(' ');
  if (
    // TODO-ZEN-804 Suppress findDOMNode deprecation warnings from external dependencies (landscape Popover)
    message.includes('findDOMNode is deprecated') ||
    // TODO-ZEN-804 Suppress generic React prop type warnings.
    (/Failed prop type: Invalid prop/).test(message) ||
    (/Invalid prop .* supplied to/).test(message) ||
    // TODO-ZEN-804 Suppress legacy context API warnings from external dependencies (landscape TransitionGroup, react-dates withStyles)
    message.includes('uses the legacy childContextTypes') ||
    message.includes('uses the legacy contextTypes') ||
    message.includes('unmountComponentAtNode is deprecated') ||
    // TODO-BB0 Redux socket.io dispatches invalid actions when server sends empty/malformed messages
    message.includes('Actions must be plain objects') ||
    // camlough-react-wordcloud defaultProps (see ZEN-2416 comment)
    (message.includes('Support for defaultProps') && args.some(a => String(a) === 'Wordcloud'))
  ) {
    return;
  }
  originalConsoleError(...args);
};

console.warn = (...args) => {
  const message = args.join(' ');
  if (
    // TODO-ZEN-804 Suppress legacy context API warnings from external dependencies (landscape TransitionGroup)
    message.includes('uses the legacy childContextTypes') ||
    // TODO-ZEN-804 Suppress deprecated lifecycle warnings from external dependencies (react-dates withStyles)
    message.includes('componentWillReceiveProps has been renamed') ||
    message.includes('componentWillUpdate has been renamed')
  ) {
    return;
  }
  originalConsoleWarn(...args);
};

import React from 'react';
import { createRoot } from 'react-dom/client';
import { unmountComponentAtNode } from 'react-dom';
import intersectionWith from 'lodash/intersectionWith';
import isEqual from 'lodash/isEqual';
import sqlFormatter from 'sql-formatter-plus';

import { createStore } from './store';
import { Provider } from 'react-redux';

import { browserHistory, Router } from 'react-router';
import { syncHistoryWithStore } from 'react-router-redux';

import setupLogRocketReact from 'logrocket-react';

import getRoutes from 'routes';
import { Light, Dark } from 'themes';

import * as components from 'components';
import * as containers from 'containers';
import _ from 'lodash';

import { AUTHOR_GROUPS } from 'zignal-config';

import PeakDetection from 'zignal-widgets/lib/PeakDetection';
import TotalUniqueAuthors from 'zignal-widgets/lib/TotalUniqueAuthors';
import TopAuthorsByEngagement from 'zignal-widgets/lib/TopAuthorsByEngagement';
import TopStoriesByScore from 'zignal-widgets/lib/TopStoriesByScore';
import ImpactScore from 'zignal-widgets/lib/ImpactScore';
import ImpactScoreOverTime from 'zignal-widgets/lib/ImpactScoreOverTime';
import MediaQualityScoreOverTime from 'zignal-widgets/lib/MediaQualityScoreOverTime';
import TopIssues from 'zignal-widgets/lib/TopIssues';
import TopSitesByMentions from 'zignal-widgets/lib/TopSitesByMentions';
import TopSitesByShares from 'zignal-widgets/lib/TopSitesByShares';
import TopSubRedditsByMentions from 'zignal-widgets/lib/TopSubRedditsByMentions';
import MediaBreakdown from 'zignal-widgets/lib/MediaBreakdown';
import TotalMentions from 'zignal-widgets/lib/TotalMentions';
import PotentialImpressions from 'zignal-widgets/lib/PotentialImpressions';
import ImpressionsBySource from 'zignal-widgets/lib/ImpressionsBySource';
import HistoricalSentiment from 'zignal-widgets/lib/HistoricalSentiment';
import TotalMentionsChart from 'zignal-widgets/lib/TotalMentionsChart';
import TotalMentionsChartBySource from 'zignal-widgets/lib/TotalMentionsChartBySource';
import TotalMentionsChartByIssue from 'zignal-widgets/lib/TotalMentionsChartByIssue';
import ZignalInsights from 'zignal-widgets/lib/ZignalInsights';
import CustomIFrame from 'zignal-widgets/lib/CustomIFrame';
import PopularPosts from 'zignal-widgets/lib/PopularPosts';
import PopularRedditPostsByComments from 'zignal-widgets/lib/PopularRedditPostsByComments';
import PopularTweets from 'zignal-widgets/lib/PopularTweets';
import TrendingStories from 'zignal-widgets/lib/TrendingStories';
import NetSentiment from 'zignal-widgets/lib/NetSentiment';
import RecentStories from 'zignal-widgets/lib/RecentStories';
import ShareOfVoiceByPotentialImpressions from 'zignal-widgets/lib/ShareOfVoiceByPotentialImpressions';
import SiteNavigationProvider from 'zignal-widgets/lib/SiteNavigationProvider';
import MediaScore from 'zignal-widgets/lib/MediaScore';
import Atlas from 'zignal-widgets/lib/Atlas';
import AverageCounts from 'zignal-widgets/lib/AverageCounts';
import StoryActions from 'zignal-widgets/lib/StoryActions';
import SentimentSlider from 'zignal-widgets/lib/SentimentSlider';
import RealTimeMentionsChart from 'zignal-widgets/lib/RealTimeMentionsChart';
import TopHashtags from 'zignal-widgets/lib/TopHashtags';
import TopLanguages from 'zignal-widgets/lib/TopLanguages';
import ThreatLevel from 'zignal-widgets/lib/ThreatLevel';
import IssueSentimentBarChart from 'zignal-widgets/lib/IssueSentimentBarChart';
import EnvironmentProvider from 'zignal-widgets/lib/EnvironmentProvider';
import TotalMentionsBarChart from 'zignal-widgets/lib/TotalMentionsBarChart';
import PotentialImpressionsChartBySource from 'zignal-widgets/lib/PotentialImpressionsChartBySource';
import TopMediaBarChart from 'zignal-widgets/lib/TopMediaBarChart';
import SmartFilterDropdown from 'zignal-widgets/lib/SmartFilterDropdown';
import SentimentBreakdown from 'zignal-widgets/lib/SentimentBreakdown';
import IssuesPieChart from 'zignal-widgets/lib/IssuesPieChart';
import WidgetSettings from 'zignal-widgets/lib/WidgetSettings';
import CustomAttributeBarChart from 'zignal-widgets/lib/CustomAttributeBarChart';
import ShareOfVoicePieChart from 'zignal-widgets/lib/ShareOfVoicePieChart';
import TopAuthorsByMentions from 'zignal-widgets/lib/TopAuthorsByMentions';
import TopAuthorsByInfluence from 'zignal-widgets/lib/TopAuthorsByInfluence';
import WordCloud from 'zignal-widgets/lib/WordCloud';
import RealtimeWordCloud from 'zignal-widgets/lib/RealtimeWordCloud';
import TotalMentionsByEntity from 'zignal-widgets/lib/TotalMentionsByEntity';
import FreeformText from 'zignal-widgets/lib/FreeformText';
import StoryList from 'zignal-landscape/lib/StoryList';
import AnnotationsLegacy from 'zignal-widgets/lib/AnnotationsLegacy';
import RealTimeStockTicker from 'zignal-widgets/lib/RealTimeStockTicker';
import TimeSeriesExplorer from 'zignal-widgets/lib/TimeSeriesExplorer';
import SentimentBreakdownByProfileChart from 'zignal-widgets/lib/SentimentBreakdownByProfileChart';
import TopStoriesByPotentialImpressions from 'zignal-widgets/lib/TopStoriesByPotentialImpressions';
import TopLocations from 'zignal-widgets/lib/TopLocations';
import TopMediaOutlets from 'zignal-widgets/lib/TopMediaOutlets';
import TopEntitiesDonutChart from 'zignal-widgets/lib/TopEntitiesDonutChart';
import EntityWheelChart from 'zignal-widgets/lib/EntityWheelChart';

// Legacy

// Landscape Icons Used Directly By CoffeeScript
import Add3Icon from 'zignal-landscape/lib/Icon/Add3Icon';
import BlogsIcon from 'zignal-landscape/lib/Icon/BlogsIcon';
import BroadcastIcon from 'zignal-landscape/lib/Icon/BroadcastIcon';
import DocumentsIcon from 'zignal-landscape/lib/Icon/DocumentsIcon';
import FacebookIcon from 'zignal-landscape/lib/Icon/FacebookIcon';
import FilterIcon from 'zignal-landscape/lib/Icon/FilterIcon';
import HighlightIcon from 'zignal-landscape/lib/Icon/HighlightIcon';
import ForumIcon from 'zignal-landscape/lib/Icon/ForumIcon';
import DarkWebIcon from 'zignal-landscape/lib/Icon/DarkWebIcon';
import InstagramIcon from 'zignal-landscape/lib/Icon/InstagramIcon';
import InformationIcon from 'zignal-landscape/lib/Icon/InformationIcon';
import LexisNexisIcon from 'zignal-landscape/lib/Icon/LexisNexisIcon';
import NewsIcon from 'zignal-landscape/lib/Icon/NewsIcon';
import QaIcon from 'zignal-landscape/lib/Icon/QaIcon';
import RedditIcon from 'zignal-landscape/lib/Icon/RedditIcon';
import ReviewIcon from 'zignal-landscape/lib/Icon/ReviewIcon';
import SyndicationIcon from 'zignal-landscape/lib/Icon/SyndicationIcon';
import IFrameIcon from 'zignal-landscape/lib/Icon/IFrameIcon';
import TrendingIcon from 'zignal-landscape/lib/Icon/TrendingIcon';
import TriangleOutlinedIcon from 'zignal-landscape/lib/Icon/TriangleOutlinedIcon';
import TwitterIcon from 'zignal-landscape/lib/Icon/TwitterIcon';
import VideosIcon from 'zignal-landscape/lib/Icon/VideosIcon';
import GearsIcon from 'zignal-landscape/lib/Icon/GearsIcon';
import MailIcon from 'zignal-landscape/lib/Icon/MailIcon';
import Remove2Icon from 'zignal-landscape/lib/Icon/Remove2Icon';
import ShowIcon from 'zignal-landscape/lib/Icon/ShowIcon';
import SettingsIcon from 'zignal-landscape/lib/Icon/SettingsIcon';
import ArrowLeftIcon from 'zignal-landscape/lib/Icon/ArrowLeftIcon';
import ArrowRightIcon from 'zignal-landscape/lib/Icon/ArrowRightIcon';
import AddToGroupIcon from 'zignal-landscape/lib/Icon/AddToGroupIcon';
import BanIcon from 'zignal-landscape/lib/Icon/BanIcon';
import WeiboIcon from 'zignal-landscape/lib/Icon/WeiboIcon';
import VkIcon from 'zignal-landscape/lib/Icon/VkIcon';
import ThreatHighlightingDisabledIcon from 'zignal-landscape/lib/Icon/ThreatHighlightingDisabledIcon';
import ThreatHighlightingEnabledIcon from 'zignal-landscape/lib/Icon/ThreatHighlightingEnabledIcon';
import StoryEmbeddedIcon from 'zignal-landscape/lib/Icon/StoryEmbeddedIcon';
import * as PlatformIcon from 'zignal-landscape/lib/PlatformIcon';

const zignalWidgetsComponents = {
  PeakDetection,
  TotalUniqueAuthors,
  TopAuthorsByEngagement,
  TopStoriesByScore,
  ImpactScore,
  ImpactScoreOverTime,
  MediaQualityScoreOverTime,
  TopSitesByMentions,
  TopSitesByShares,
  TopSubRedditsByMentions,
  MediaBreakdown,
  TotalMentions,
  TopIssues,
  PotentialImpressions,
  HistoricalSentiment,
  TotalMentionsChart,
  TotalMentionsChartBySource,
  TotalMentionsChartByIssue,
  ZignalInsights,
  PopularPosts,
  PopularRedditPostsByComments,
  PopularTweets,
  TrendingStories,
  NetSentiment,
  MediaScore,
  RecentStories,
  ShareOfVoiceByPotentialImpressions,
  ImpressionsBySource,
  Atlas,
  AverageCounts,
  StoryActions,
  SentimentSlider,
  RealTimeMentionsChart,
  TopHashtags,
  TopLanguages,
  ThreatLevel,
  TotalMentionsBarChart,
  IssueSentimentBarChart,
  PotentialImpressionsChartBySource,
  TopMediaBarChart,
  SmartFilterDropdown,
  SentimentBreakdown,
  WidgetSettings,
  CustomAttributeBarChart,
  IssuesPieChart,
  ShareOfVoicePieChart,
  TopAuthorsByMentions,
  TopAuthorsByInfluence,
  CustomIFrame,
  WordCloud,
  RealtimeWordCloud,
  TotalMentionsByEntity,
  FreeformText,
  StoryList,
  AnnotationsLegacy,
  RealTimeStockTicker,
  TimeSeriesExplorer,
  SentimentBreakdownByProfileChart,
  TopStoriesByPotentialImpressions,
  TopLocations,
  TopMediaOutlets,
  TopEntitiesDonutChart,
  EntityWheelChart
};

// Import landscape components here.
import ThemeProvider from 'zignal-landscape/lib/ThemeProvider';
import ComparisonDateRangePicker from 'zignal-landscape/lib/ComparisonDateRangePicker';
import ConfirmationModal from 'zignal-landscape/lib/ConfirmationModal';
import DateRangePicker from 'zignal-landscape/lib/DateRangePicker';
import FetchHookProvider from 'zignal-widgets/lib/FetchHookProvider';
import StoryActionsProvider from 'zignal-widgets/lib/StoryActionsProvider';
import AlignJustifiedIcon from 'zignal-landscape/lib/Icon/AlignJustifiedIcon';

const zignalLandscapeComponents = {
  Add3Icon,
  BlogsIcon,
  BroadcastIcon,
  ComparisonDateRangePicker,
  ConfirmationModal,
  DocumentsIcon,
  FacebookIcon,
  FilterIcon,
  ForumIcon,
  DarkWebIcon,
  GearsIcon,
  HighlightIcon,
  InstagramIcon,
  InformationIcon,
  LexisNexisIcon,
  NewsIcon,
  QaIcon,
  RedditIcon,
  ReviewIcon,
  SyndicationIcon,
  IFrameIcon,
  ThemeProvider,
  AlignJustifiedIcon,
  TrendingIcon,
  TriangleOutlinedIcon,
  TwitterIcon,
  VideosIcon,
  MailIcon,
  Remove2Icon,
  ShowIcon,
  SettingsIcon,
  DateRangePicker,
  ArrowLeftIcon,
  ArrowRightIcon,
  AddToGroupIcon,
  BanIcon,
  WeiboIcon,
  VkIcon,
  ThreatHighlightingDisabledIcon,
  ThreatHighlightingEnabledIcon,
  StoryEmbeddedIcon,
  ...PlatformIcon
};

// Detect if component uses any of these key if is used inject those providers
const detectKeysInProvider = (keys) => {
  const detectKeys = [
    'accountId',
    'beforeFetchHook',
    'campaignId',
    'campaignIds',
    'defaultCampaignEsTagId',
    'esTagIds',
    'changeSentimentInfo',
    'fetchHookProps',
    'hasWorkBenchFlag',
    'isResponseStaleFetchHook',
    'maxReadingLists',
    'maxStoriesPerList',
    'profileMap',
    'readingListInfo',
    'toastr',
    'user',
    'userId',
    'getActionsDropdown'
  ];

  return intersectionWith(keys, detectKeys, isEqual).length > 0;
};

// Detect if the property includeAccountData is passed as a prop this force the render
// to include the providers used to load account and user information
const detectIncludeAccountData = (options) => {
  const { includeAccountData: includeAccountDataCoffee } = options.detail;
  const { includeAccountData } = options.detail.props;

  return includeAccountDataCoffee || includeAccountData;
};

/**
 * Backbone views don't have direct access
 * to process.env.NODE_ENV. With the following
 * we provide them the global variable. Yuck!
 */
window.process = {
  env: {
    NODE_ENV: process.env.NODE_ENV
  }
};

let store;
if (process.env.NODE_ENV === 'development') {
  store = createStore(browserHistory,
    window.__REDUX_DEVTOOLS_EXTENSION__ && window.__REDUX_DEVTOOLS_EXTENSION__());
} else {
  store = createStore(browserHistory, window.__data);
}

if (window.LogRocket) {
  setupLogRocketReact(window.LogRocket);
}

// expose getState so backbone can access data in store
window.getReduxStore = store.getState;
window.dispatchReduxAction = store.dispatch;

//ENG-13052 SUNet DanaURL is not working for window.location
window.assignUrl = (url) => {
  window.location = typeof DanaUrl === 'function' ? DanaUrl(url) : url; // eslint-disable-line
};

const history = syncHistoryWithStore(browserHistory, store);

const dest = document.getElementById('root');

const routerComponent = (
  <Router history={history}>
    {getRoutes()}
  </Router>
);

const theme = window.location.hostname.indexOf('controller') > -1 ? Dark : Light;

const root = createRoot(dest);
root.render(
  <Provider store={store} key="provider">
    <ThemeProvider theme={theme}>
      {routerComponent}
    </ThemeProvider>
  </Provider>
);

const beforeFetchHookHandler = (id) => {
  return () => {
    // publish an event like Views.Widget does
    window.Vent.trigger('widget:fetch', id);
  };
};

const afterFetchHookHandler = (id) => {
  return () => {
    // publish an event like Views.Widget does
    window.Vent.trigger('widget:fetch_finished', id);
  };
};

const failedFetchHookHandler = (id) => {
  return () => {
    // publish an event like Views.Widget does
    window.Vent.trigger('widget:fetch_failed', id);
  };
};

// Frontend needs global awareness of
// - when we are about to make an AJAX request
// - whether the response is considered "stale" or not
const getFetchHookProps = (view) => {
  return {
    beforeFetchHook: beforeFetchHookHandler(view.props.id),
    afterFetchHook: afterFetchHookHandler(view.props.id),
    failedFetchHook: failedFetchHookHandler(view.props.id),
    isResponseStaleFetchHook: window.Account.staleQueryHelper.isResponseStaleFetchHook
  };
};

const accountProviderComponent = (config) => {
  const {
    widgetTheme,
    Component,
    view,
    isReport,
    isReadOnlyDashboard,
    isZCC,
    isEmbeddedWidget,
    showStoryLocation
  } = config;

  const readingListParams = Account.getReadingListsForCampaign(Account.getActiveCampaign().id);
  const readingListProps = {
    maxReadingLists: readingListParams ? readingListParams.max_reading_lists : 0,
    maxStoriesPerList: readingListParams ? readingListParams.max_stories_per_list : 0,
    campaignId: Account.getActiveCampaign().get('id'),
    updateReadingListsExternal: (payload) => {
      if (typeof Account !== 'undefined') {
        const readingModel = Account.getReadingListsForCampaign(Account.getActiveCampaign().id);
        if (readingModel) {
          readingModel.add(payload);
          readingModel.trigger('updateActionMenu');
          Vent.trigger('create:reading_list', payload);
        }
      }
    }
  };

  const subscribedDatasets = (Account.getActiveCampaign().get('candidates')?.toJSON() || []).filter((p) => p.subscribesTo);
  const curatedDatasets = Account.get('curatedDatasets') || [];

  const changeSentimentProps = {
    isVirtual: Account.getActiveCampaign().get('isVirtual'),
    profileMap: (Account.get('profiles_for_account') || []).reduce((prev, cur) => {
      prev[cur._id] = cur.name;
      return prev;
    }, {}),
    hasWorkBenchFlag: Account.getActiveCampaign().hasWorkbenchFlag(),
    // Lazily read campaign IDs at request time so stale widget mounts don't send empty arrays.
    getCampaignIds: () => {
      const latestSubscribedDatasets = (Account.getActiveCampaign().get('candidates')?.toJSON() || []).filter((p) => p.subscribesTo);
      const latestCuratedDatasets = Account.get('curatedDatasets') || [];
      const latestSubscribedCampaignIds = latestSubscribedDatasets.reduce((ids, dataset) => {
        const curatedDataset = latestCuratedDatasets.find((cd) => cd.id === dataset.subscribesTo);
        if (curatedDataset?.campaignId) { ids.push(curatedDataset.campaignId); }
        return ids;
      }, []);
      const campaignsForUser = Account.get('campaigns_for_user') || [];
      return Array.from(new Set(
        campaignsForUser.map((campaign) => campaign._id).concat(latestSubscribedCampaignIds)
      ));
    },
    subscribedProfileIds: subscribedDatasets.map((dataset) => dataset.id),
    // { curatedProfileId -> subscribedProfileId } — used by the saga to remap API response IDs
    curatedToSubscribed: subscribedDatasets.reduce((obj, dataset) => {
      obj[dataset.subscribesTo] = dataset.id;
      return obj;
    }, {}),
    // eslint-disable-next-line no-undef
    toastr
  };

  const getStoryModels = (stories) =>
    stories.map((storyObject) => {
      if (typeof storyObject.get === 'function') {
        return storyObject;
      }
      // eslint-disable-next-line no-undef
      return (new Models.Story(storyObject));
    });

  const copyToAnnotationProps = {
    copyToAnnotationExternal: (stories) => Vent.trigger('copyToAnnotation:stories', getStoryModels(stories))
  };

  const addToGroupProps = {
    addToGroupsExternal: (data, onBulkCancel) => { // called from zignal-widgets
      const activeAccountId = Account.getActiveCampaign().get('account');
      const campaignListByAccount = Account.getCampaignsForAccountID(activeAccountId)
        .filter((campaign) => campaign.get('active'))
        .map((campaign) => ({
          value: campaign.get('id'),
          label: campaign.get('title'),
          groupLimit: campaign.get('groupLimit'),
          groupAuthorLimit: campaign.get('groupAuthorLimit'),
          groupDomainLimit: campaign.get('groupDomainLimit'),
          groupMediaOutletLimit: campaign.get('groupMediaOutletLimit')
        })
        );

      const defaultCampaign = campaignListByAccount
        .find((campaign) => campaign.value === window.Account.getActiveCampaign().get('id'));

      const groupFieldsDestination = data.map(d => {
        const authorGroups = [];
        AUTHOR_GROUPS.forEach(ag => {
          if (ag.sources.includes(d.source) && ['author', 'channel'].includes(ag.type)) {
            ag.targetValue = d.authorUsername;
            authorGroups.push(ag);
          }
        });
        return authorGroups;
      }).flat();

      // eslint-disable-next-line no-undef
      window.addToGroup ?.close();
      // eslint-disable-next-line no-undef
      window.addToGroup ?.remove();
      // eslint-disable-next-line no-undef
      window.addToGroup = new Views.BaseReact({
        component: 'containers/Groups',
        el: document.getElementById('group-modal-content'),
        props: {
          campaigns: campaignListByAccount,
          defaultCampaign,
          groupFieldsDestination,
          onSuccess: (result) => {
            onBulkCancel();
            Account.toastrNotifications._successMessage({
              success_title: 'Added To Group',
              success_message: `Successfully added to ${result.params.bodyParams.name}`
            });
          },
          onError: () => {
            Account.toastrNotifications._errorMessage({
              error_title: 'Could not save to group',
              error_message: 'Please try again later'
            });
          }
        }
      });
      window.addToGroup.render();
    }
  };

  return React.createElement(Provider, { store, key: 'provider' },
    React.createElement(StoryActionsProvider,
      {
        readingListInfo: readingListProps,
        changeSentimentInfo: changeSentimentProps,
        copyToAnnotationInfo: copyToAnnotationProps,
        addToGroupInfo: addToGroupProps
      },
      React.createElement(FetchHookProvider, getFetchHookProps(view),
        React.createElement(ThemeProvider, { theme: widgetTheme, scaledFonts: isZCC },
          React.createElement(SiteNavigationProvider, {},
            React.createElement(EnvironmentProvider,
              { isReport, isZCC, isReadOnlyDashboard, isEmbeddedWidget, shouldLazyLoad: true, showStoryLocation },
              React.createElement(Component, view.props)
            )
          )
        )
      )
    )
  );
};

const createComponent = (config) => {
  const {
    widgetTheme,
    Component,
    view,
    isReport,
    isZCC,
    isEmbeddedWidget,
    showStoryLocation
  } = config;

  return React.createElement(Provider, { store, key: 'provider' },
    React.createElement(FetchHookProvider, getFetchHookProps(view),
      React.createElement(ThemeProvider, { theme: widgetTheme, scaledFonts: isZCC },
        React.createElement(SiteNavigationProvider, {},
          React.createElement(EnvironmentProvider, { isReport, isZCC, isEmbeddedWidget, shouldLazyLoad: true, showStoryLocation },
            React.createElement(Component, view.props)
          )
        )
      )
    )
  );
};

// @see Views.BaseReact
// eslint-disable-next-line complexity
document.body.addEventListener('react:render', (options) => {
  const view = options.detail;
  const { el, component } = view; // { root Element, Component name }
  const viewOptions = view.options;

  const includeAccountData = detectIncludeAccountData(options);
  const includedInProviders = detectKeysInProvider(Object.keys(options.detail.props));

  const [componentPrefix, componentSuffix] = component.split('/');
  let Component;

  switch (componentPrefix) {
    case 'components':
      Component = components[componentSuffix];
      break;
    case 'containers':
      Component = containers[componentSuffix];
      break;
    case 'zignal-landscape':
      Component = zignalLandscapeComponents[componentSuffix];
      break;
    case 'zignal-widgets':
      Component = zignalWidgetsComponents[componentSuffix];
      break;
    default:
      console.warn('\'Views.BaseReact\' component without prefix is deprecated. ' + // eslint-disable-line no-console
        'Make sure you are referencing \'components/ComponentName\', ' +
        '\'containers/ComponentName\', \'zignal-landscape/ComponentName\' ' +
        'in your backbone view.');
      Component = components[component];
  }

  /* eslint-disable no-undef */
  const isReport = window.Helper.onRenderedReportPage();
  const isReadOnlyDashboard = Account.get('user').isDashboardUser() || Account.get('user').isNTKUser();
  const isZCC = document.body.className.indexOf('isCommandCenter') > -1;
  const widgetTheme = isZCC ? Dark : Light;
  const isEmbeddedWidget = componentSuffix === 'WidgetEmbedModal'; //  This is the only component that needs this
  // Via EnvironmentProvider so the Newsroom drill-down (inside ~35 widgets) is covered without per-mount props.
  // When the ZEN_2890_Story_Location split is retired, remove showStoryLocation from here and every consumer.
  const showStoryLocation = typeof window.splitHelper !== 'undefined'
    && window.splitHelper.getTreatment('ZEN_2890_Story_Location') === 'on';

  const config = {
    widgetTheme,
    Component,
    view,
    isZCC,
    isReadOnlyDashboard,
    isReport,
    isEmbeddedWidget,
    showStoryLocation
  };

  let element = null;

  if (includedInProviders || includeAccountData) {
    element = accountProviderComponent(config);
  }
  else {
    element = createComponent(config);
  }

  if (el){
    if (!viewOptions.stateful) {
      // Only unmount stateless / controlled Components.
      // Store root on the element to reuse it and avoid creating multiple roots
      if (el._reactRoot) {
        el._reactRoot.unmount();
        el._reactRoot = null;
      }
    }
    // Reuse existing root or create new one
    if (!el._reactRoot) {
      el._reactRoot = createRoot(el);
    }
    el._reactRoot.render(React.createElement(components.ErrorBoundary, null, element));
  }
}, false);

document.body.addEventListener('react:unmount', (options = {}) => {
  const { detail } = options;
  const { el } = detail;
  if (el && el._reactRoot) {
    el._reactRoot.unmount();
    el._reactRoot = null;
  }
}, false);

window.sqlFormatter = sqlFormatter;
window._ = _;
